/**
 * Speaker profiles -- group 12 (panel dipoles, plus one box control).
 *
 * Seven of these eight radiate as dipoles from a diaphragm with almost no mass,
 * which changes three things the engine cares about: the impedance is not a
 * damped resonant circuit but either a capacitive load or a near-resistive one,
 * there is no baffle step to compensate, and the rear wave means room coupling
 * dominates the perceived low end. The eighth entry is a modern ported box with
 * controlled directivity, present as the control condition. Voicing offsets are
 * authored per design and are the part that is not derived.
 */

import type { SpeakerProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, makeSpeaker, usd } from '../kit';

export const SPEAKERS_12: SpeakerProfile[] = [
  makeSpeaker({
    mfr: 'Magnepan',
    model: '3.7i',
    price: usd(6600, 'APPROXIMATE', 2022, 'Approximate US retail per pair; Magnepan pricing moved several times over the production run.'),
    year: 2015,
    country: 'United States',
    speakerType: 'planar-magnetic',
    enclosure: 'dipole-panel',
    drivers: ['planar-panel', 'ribbon'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 86,
    nominalOhms: 4,
    minOhms: 3.4,
    bass: [35, 30],
    hfExtension: 40000,
    crossovers: [
      { freqHz: 350, slopeDbOct: 6, alignment: 'first-order' },
      { freqHz: 1700, slopeDbOct: 6, alignment: 'first-order' },
    ],
    responseTilt: [[20, 0], [45, -0.6], [100, 0.4], [300, -0.3], [900, 0.3], [2500, 0.8], [6000, 0.4], [12000, 0.7], [20000, -0.8]],
    coverageAngleDeg: 40,
    recommendedWatts: [100, 400],
    dimensionsMm: [1780, 610, 41],
    massKg: 32,
    thdPercent: 0.3,
    harmonicShape: HARMONIC_SHAPES.planar,
    roomCoupling: 'high',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9ec5d8',
    tags: ['dipole', 'quasi-ribbon', 'true-ribbon-tweeter', 'resistive-load'],
    notes:
      'Quasi-ribbon bass and midrange panels crossed to a true ribbon with simple first-order networks, which keeps the crossover phase behaviour gentle but leaves broad driver overlap through the transition regions. Electrically this is the least demanding panel here despite the reputation: the load is close to purely resistive around four ohms, so amplifier output impedance changes level but barely changes response shape. The large radiating area means excursion is tiny for a given SPL, so low-frequency distortion stays low right down to the dipole roll-off, below which output falls away with no port or box resonance to prop it up.',
    provenanceNote:
      'Sensitivity, nominal impedance, quoted bass extension, panel dimensions and driver complement from Magnepan published specifications. Crossover frequencies and slopes are inferred from the published three-way first-order description, not documented values, and the voicing offsets are authored. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Quad',
    model: 'ESL-2812',
    price: usd(11000, 'APPROXIMATE', 2022, 'Approximate US retail per pair; the ESL-2812 has been sold at a range of prices across distributors.'),
    year: 2013,
    country: 'United Kingdom',
    speakerType: 'electrostatic',
    enclosure: 'dipole-panel',
    drivers: ['electrostatic-panel'],
    driverCount: 1,
    wayCount: 1,
    sensitivityDb: 86,
    nominalOhms: 8,
    minOhms: 1.6,
    bass: [37, 32],
    hfExtension: 21000,
    crossovers: [],
    responseTilt: [[20, 0], [50, -0.6], [150, 0.3], [400, 0.5], [1000, 0], [3000, -0.7], [6000, -0.4], [10000, -1.2], [20000, -3.0]],
    coverageAngleDeg: 50,
    recommendedWatts: [50, 100],
    dimensionsMm: [1040, 690, 385],
    massKg: 26,
    thdPercent: 0.15,
    roomCoupling: 'high',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c9c2a8',
    tags: ['full-range-panel', 'concentric-delay-rings', 'no-crossover', 'capacitive-load'],
    notes:
      'A single full-range diaphragm driven through concentric annular electrodes with progressive delay, so the radiating area shrinks with frequency and the source approximates a point behind the panel rather than a line. Because there is no crossover anywhere in the audio band there is no crossover phase rotation to model, which is the single biggest structural difference between this and every multi-way design in the group. Electrically it is a capacitor: impedance falls from around thirty ohms at the low end to under two ohms at the top, so an amplifier with any appreciable output impedance will show a falling treble on this load, and the protection circuit limits how hard it can be driven before it clamps.',
    provenanceNote:
      'Sensitivity, dimensions, mass, quoted bandwidth and the single-panel delay-line construction from Quad published specifications. The minimum impedance figure reflects the well-documented capacitive behaviour of this panel family; the exact value and the voicing offsets are authored rather than measured. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Sound Lab',
    model: 'Majestic 745',
    price: usd(30000, 'APPROXIMATE', 2022, 'Approximate US retail per pair; Sound Lab builds to order and pricing varies with finish and panel height.'),
    year: 2015,
    country: 'United States',
    speakerType: 'electrostatic',
    enclosure: 'dipole-panel',
    drivers: ['electrostatic-panel'],
    driverCount: 1,
    wayCount: 1,
    sensitivityDb: 86,
    nominalOhms: 8,
    minOhms: 1.4,
    bass: [25, 21],
    hfExtension: 22000,
    crossovers: [],
    responseTilt: [[20, 0.8], [40, 0.5], [100, -0.4], [300, 0.2], [800, -0.3], [2000, 0.4], [5000, -0.6], [10000, -1.0], [20000, -2.2]],
    coverageAngleDeg: 45,
    recommendedWatts: [100, 700],
    dimensionsMm: [2130, 1020, 305],
    massKg: 92,
    thdPercent: 0.12,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#a8b8a0',
    tags: ['full-range-panel', 'curved-stator', 'step-up-transformer', 'line-source'],
    notes:
      'A very large curved-stator full-range panel, which trades the point-source behaviour of the Quad for radiating area: the same SPL is produced with far less diaphragm excursion, and the low-frequency limit moves down about a third of an octave without any dynamic driver being involved. Curvature sets horizontal dispersion mechanically rather than by crossover design, so the pattern stays roughly constant with frequency instead of narrowing steadily. The step-up transformer that feeds the panel is what the amplifier actually sees, and the resulting load swings from tens of ohms at the bottom to under two ohms at the top, which is a harder current demand than the Quad because the panel is much larger.',
    provenanceNote:
      'Panel construction, curved stator geometry, backplate controls and approximate size from Sound Lab published material. Sound Lab does not publish a conventional sensitivity, impedance or distortion table for this model, so those figures are estimates typical of large full-range electrostatic panels; dimensions and price are approximate and configuration-dependent.',
  }),

  makeSpeaker({
    mfr: 'Analysis Audio',
    model: 'Omega',
    price: usd(30000, 'APPROXIMATE', 2021, 'Approximate US retail per pair; Analysis Audio is built to order and priced through a small distributor network.'),
    year: 2012,
    country: 'Greece',
    speakerType: 'planar-magnetic',
    enclosure: 'dipole-panel',
    drivers: ['planar-panel'],
    driverCount: 2,
    wayCount: 2,
    sensitivityDb: 86,
    nominalOhms: 4,
    minOhms: 1.8,
    bass: [28, 24],
    hfExtension: 30000,
    crossovers: [{ freqHz: 220, slopeDbOct: 12, alignment: 'butterworth' }],
    responseTilt: [[20, 0], [60, 0.6], [180, -0.5], [500, 0.3], [1500, -0.4], [4000, 0.5], [8000, 0.2], [14000, -0.6], [20000, -1.8]],
    coverageAngleDeg: 35,
    recommendedWatts: [150, 600],
    dimensionsMm: [1980, 700, 110],
    massKg: 62,
    thdPercent: 0.25,
    harmonicShape: HARMONIC_SHAPES.planar,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#b98fc0',
    tags: ['curved-panel', 'two-way-planar', 'low-impedance', 'current-hungry'],
    notes:
      'Curved planar-magnetic panels with a single low crossover, so the entire midrange and treble come from one continuous diaphragm and there is no discontinuity anywhere in the presence region. The curvature widens horizontal dispersion relative to a flat panel of the same area, which changes the direct-to-reverberant balance the room model computes rather than the on-axis curve. The reason this design has a reputation for eating amplifiers is not the nominal four ohms but the minimum: it sits below two ohms over a wide band, and unlike an electrostatic that minimum is not confined to the top octave, so current demand is sustained rather than transient.',
    provenanceNote:
      'Planar-magnetic construction, curved panel geometry and approximate size from Analysis Audio published material. The manufacturer does not publish a full impedance, sensitivity or distortion table, so those figures are estimates typical of large low-impedance planar dipoles; the crossover point and the voicing offsets are authored. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Alsyvox',
    model: 'Tintoretto',
    price: usd(48000, 'APPROXIMATE', 2022, 'Approximate US retail per pair; Alsyvox pricing varies by finish and by market.'),
    year: 2018,
    country: 'Spain',
    speakerType: 'planar-magnetic',
    enclosure: 'dipole-panel',
    drivers: ['planar-panel', 'ribbon'],
    driverCount: 4,
    wayCount: 3,
    sensitivityDb: 92,
    nominalOhms: 4,
    minOhms: 2.6,
    bass: [24, 20],
    hfExtension: 40000,
    crossovers: [
      { freqHz: 150, slopeDbOct: 12, alignment: 'linkwitz-riley' },
      { freqHz: 1300, slopeDbOct: 6, alignment: 'first-order' },
    ],
    responseTilt: [[20, 0.3], [50, 0.7], [120, -0.3], [400, -0.5], [1200, 0.2], [3000, 0.6], [7000, 0.3], [13000, 0.5], [20000, -0.5]],
    coverageAngleDeg: 40,
    recommendedWatts: [20, 300],
    dimensionsMm: [1740, 1150, 190],
    massKg: 135,
    thdPercent: 0.2,
    harmonicShape: HARMONIC_SHAPES.planar,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#d8a06a',
    tags: ['high-sensitivity-planar', 'large-bass-panel', 'ribbon-treble', 'valve-friendly'],
    notes:
      'The parameter that separates this from every other panel here is sensitivity: a very large bass diaphragm area and strong magnet structure put it around ninety-two dB, roughly six dB above the usual planar figure, which moves it from needing hundreds of watts into the range where a low-power valve amplifier is viable. Because it is still a dipole with a four-ohm nominal load, that pairing only works if the amplifier tolerates the minimum impedance; the model keeps the load real rather than flattering it. Bass panels are crossed low and second-order while the ribbon transition stays first-order, so phase behaviour differs between the two transitions rather than being uniform.',
    provenanceNote:
      'Panel construction, ribbon treble section, high quoted sensitivity and approximate size from Alsyvox published material. Crossover frequencies and slopes are inferred from the three-way description rather than documented, and impedance minimum, distortion and the voicing offsets are estimates. Price is approximate.',
  }),

  makeSpeaker({
    mfr: 'Clarisys Audio',
    model: 'Minuet',
    price: usd(42000, 'APPROXIMATE', 2023, 'Approximate US retail per pair; sold through a small distributor network with market-dependent pricing.'),
    year: 2020,
    country: 'Vietnam',
    speakerType: 'planar-magnetic',
    enclosure: 'dipole-panel',
    drivers: ['ribbon', 'planar-panel'],
    driverCount: 3,
    wayCount: 2,
    sensitivityDb: 88,
    nominalOhms: 3,
    minOhms: 2.2,
    bass: [22, 18],
    hfExtension: 40000,
    crossovers: [{ freqHz: 300, slopeDbOct: 6, alignment: 'first-order' }],
    responseTilt: [[20, 0.4], [55, 0.2], [140, -0.4], [450, 0.3], [1300, 0.5], [3500, 0.2], [8000, 0.6], [14000, 0.4], [20000, -0.4]],
    coverageAngleDeg: 30,
    recommendedWatts: [100, 500],
    dimensionsMm: [1650, 820, 200],
    massKg: 105,
    thdPercent: 0.2,
    harmonicShape: HARMONIC_SHAPES.planar,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#7fb8a8',
    tags: ['full-range-ribbon', 'low-impedance', 'trapezoidal-panel', 'apogee-lineage'],
    notes:
      'True ribbon diaphragms rather than printed-trace planar panels, meaning the conductor and the radiating membrane are the same piece of foil, so moving mass is lower again and the high-frequency limit is set by the ribbon dimensions rather than by a suspension resonance. A single low crossover leaves the midrange and treble on one continuous ribbon. The transformer coupling that ribbons require is what produces the very low nominal impedance here, and the model keeps that: three ohms nominal falling to just over two is a load that punishes amplifiers with limited current, and the dipole radiation means the low end depends heavily on distance to the front wall.',
    provenanceNote:
      'Full-range ribbon construction, panel arrangement and approximate size from Clarisys published material. The manufacturer does not publish an impedance or distortion table, so sensitivity, minimum impedance, crossover point and the voicing offsets are estimates typical of transformer-coupled full-range ribbon dipoles. Price and country of manufacture are as understood from distributor material and are approximate.',
  }),

  makeSpeaker({
    mfr: 'Apogee Acoustics',
    model: 'Scintilla',
    price: usd(3900, 'ORIGINAL_RETAIL', 1986, 'Approximate original US retail per pair in the mid-1980s; long discontinued and now traded used or rebuilt.'),
    year: 1986,
    country: 'United States',
    speakerType: 'dipole',
    enclosure: 'dipole-panel',
    drivers: ['ribbon'],
    driverCount: 3,
    wayCount: 3,
    sensitivityDb: 78,
    nominalOhms: 1,
    minOhms: 0.8,
    bass: [28, 22],
    hfExtension: 25000,
    crossovers: [
      { freqHz: 350, slopeDbOct: 6, alignment: 'first-order' },
      { freqHz: 2500, slopeDbOct: 6, alignment: 'first-order' },
    ],
    responseTilt: [[20, 0], [50, 0.4], [140, -0.7], [400, 0.2], [1200, -0.3], [3000, 0.5], [7000, 0.3], [13000, -0.5], [20000, -1.5]],
    coverageAngleDeg: 30,
    recommendedWatts: [200, 1000],
    dimensionsMm: [1420, 940, 130],
    massKg: 55,
    thdPercent: 0.15,
    roomCoupling: 'high',
    confidence: 'ESTIMATED',
    accent: '#8f9bb3',
    tags: ['one-ohm-load', 'all-ribbon', 'low-sensitivity', 'vintage-reference'],
    notes:
      'All three sections are ribbons, and the direct-drive arrangement that avoids a step-up transformer is exactly what produces the one-ohm nominal impedance this design is remembered for. Combined with seventy-eight dB sensitivity, the current demand is extreme: reaching a normal listening level requires roughly sixteen times the current of a ninety-dB eight-ohm box, which is why the profile is useful in this database as a stress case for amplifier output impedance and current limiting rather than as a recommendation. Diaphragm mass is so low that the distortion signature is closer to an electrostatic than to a cone system, with odd orders dominating and very little level dependence until the ribbon runs out of excursion at the bottom.',
    provenanceNote:
      'Ribbon construction, the one-ohm nominal load and low sensitivity are well documented in period reviews and Apogee material; exact figures varied between production runs and the values here are estimates. Original retail price is approximate. Crossover points, distortion and the voicing offsets are authored, not measured.',
  }),

  makeSpeaker({
    mfr: 'Perlisten Audio',
    model: 'S7t',
    price: usd(17000, 'APPROXIMATE', 2023, 'Approximate US retail per pair; Perlisten pricing depends on finish.'),
    year: 2021,
    country: 'United States',
    speakerType: 'floorstander',
    enclosure: 'ported',
    drivers: ['beryllium-dome', 'dynamic'],
    driverCount: 7,
    wayCount: 3,
    sensitivityDb: 92,
    nominalOhms: 4,
    minOhms: 3.2,
    bass: [26, 22],
    hfExtension: 37000,
    portTuningHz: 24,
    crossovers: [
      { freqHz: 480, slopeDbOct: 24, alignment: 'linkwitz-riley' },
      { freqHz: 2200, slopeDbOct: 24, alignment: 'linkwitz-riley', offsetMm: 18 },
    ],
    responseTilt: [[20, 0], [60, 0.3], [150, -0.2], [500, 0.1], [1200, -0.3], [3000, 0.4], [8000, 0.2], [14000, -0.3], [20000, -0.9]],
    resonances: [[220, -46, 20], [640, -50, 26]],
    coverageAngleDeg: 90,
    recommendedWatts: [50, 600],
    dimensionsMm: [1245, 292, 419],
    massKg: 55,
    thdPercent: 0.2,
    roomCoupling: 'medium',
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5f86b8',
    tags: ['controlled-directivity', 'dome-array', 'ported', 'box-control-condition'],
    notes:
      'Present in this group as the control condition: a braced ported box whose vertical array of one beryllium tweeter flanked by two dome midranges narrows vertical dispersion deliberately while keeping horizontal coverage wide. That is the inverse of a tall panel, which is narrow horizontally and effectively a line vertically, so the two designs load a room in opposite ways even at matched on-axis response. Fourth-order acoustic crossovers and a fourth-order ported low-frequency roll-off give steep phase rotation where the panels have almost none, and port tuning near twenty-four hertz means output below that point falls at twenty-four dB per octave with the driver unloading.',
    provenanceNote:
      'Sensitivity, impedance, driver complement, dimensions, mass and quoted bass extension from Perlisten published specifications. Crossover frequencies are approximated from the published array description, and cabinet resonance levels, port tuning and the voicing offsets are modelled rather than measured. Price is approximate.',
  }),
];
